import { hasValue } from 'src/shared/utils/common';

import { FormStore } from '../../entities/form.entity';
import { RigOperationStage } from '../../entities/rig-operations/rig-operation-stage.entity';
import { RigOperation } from '../../entities/rig-operations/rig-operation.entity';
import { calculateCommercialSpeed, calculateDurationByCommercialSpeed } from '../../utils';
import { FormPlugin } from '../abstract-form-plugin.entity';

export class GeoTaskCommercialSpeedCalculationsPlugin extends FormPlugin<FormStore> {
  connect(form: FormStore): VoidFunction | void {
    this.form = form;
    const { rigOperationsService } = form;

    const updatedRigOperationsListDisposer = form.eventBus.subscribe(
      'updatedRigOperaionList',
      this.calculateGeoTastCommercialSpeed.bind(this),
    );
    const updatedStagesListDisposer = form.eventBus.subscribe(
      'updatedStageList',
      this.calculateGeoTastCommercialSpeed.bind(this),
    );
    const stagePassingValueChangeDisposer = form.eventBus.subscribe(
      'rigOperationItemFieldValueChange',
      ({ fieldId, section }) => {
        if (fieldId === 'passingValue' && !section) {
          this.calculateGeoTastCommercialSpeed();
        }
      },
    );
    const stageDurationChangeDisposer = form.eventBus.subscribe(
      'rigOperationItemFieldValueChange',
      ({ fieldId, stage, section }) => {
        if (
          fieldId === 'duration' &&
          !section &&
          form.rigOperationsService.isDrillingStage(stage?.wellLifeCycleStageId ?? null)
        ) {
          this.calculateGeoTastCommercialSpeed();
        }
      },
    );
    const deletedRigOperationDisposer = form.eventBus.subscribe(
      'deleteRigOperation',
      this.calculateGeoTastCommercialSpeed.bind(this),
    );
    const deletedStageDisposer = form.eventBus.subscribe(
      'deleteStage',
      this.calculateGeoTastCommercialSpeed.bind(this),
    );
    const mainDataCommercialSpeedValueChangeDisposer = form.eventBus.subscribe(
      'mainDataValueChange',
      ({ fieldId, value }) => {
        if (
          !['commercialSpeed', 'passingValue'].includes(fieldId) ||
          rigOperationsService.isSectionDetailingOrManyRigOperations
        ) {
          return;
        }

        const stageDuration = rigOperationsService.rigOperations[0]?.stages?.find((st) =>
          rigOperationsService.isDrillingStage(st.wellLifeCycleStageId),
        )?.duration;

        if (!hasValue(stageDuration)) {
          this.calculateDrillingStageDurationByCommercialSpeed(value);
          return;
        }

        if (fieldId === 'commercialSpeed') {
          this.calculateDrillingStageDurationByCommercialSpeed(value);
        } else {
          this.calculateGeoTastCommercialSpeed();
        }
      },
    );

    return () => {
      updatedRigOperationsListDisposer();
      updatedStagesListDisposer();
      stagePassingValueChangeDisposer();
      deletedRigOperationDisposer();
      deletedStageDisposer();
      stageDurationChangeDisposer();
      mainDataCommercialSpeedValueChangeDisposer();
    };
  }

  private calculateGeoTastCommercialSpeed() {
    if (!this.form) {
      return;
    }

    const { rigOperationsService, fields, onValueChange } = this.form;

    const mainDataCommercialSpeedControl = fields['commercialSpeed'];

    if (!mainDataCommercialSpeedControl) {
      console.error('cannot find commercialSpeed control');
      return;
    }

    const rigOperationsDrillingDuration = rigOperationsService.rigOperations.reduce(
      (acc: number | null, currRigOperation) => {
        const rigOperationDrillingDuration = currRigOperation.stages.reduce((acc: number | null, currStage) => {
          if (rigOperationsService.hasSections(currStage.wellLifeCycleStageId)) {
            if (hasValue(acc)) {
              return acc + (currStage.duration ?? 0);
            } else {
              return currStage.duration;
            }
          }

          return acc;
        }, null);

        if (hasValue(acc)) {
          return acc + (rigOperationDrillingDuration ?? 0);
        } else {
          return rigOperationDrillingDuration;
        }
      },
      null,
    );

    if (rigOperationsService.isSectionDetailingOrManyRigOperations) {
      const rigOperationsPassingValue = rigOperationsService.rigOperations.reduce(
        (acc: number | null, currRigOperation) => {
          const rigOperationPassingValue = currRigOperation.stages.reduce((acc: number | null, currStage) => {
            if (rigOperationsService.hasSections(currStage.wellLifeCycleStageId)) {
              if (hasValue(acc)) {
                return acc + (currStage.passingValue ?? 0);
              } else {
                return currStage.passingValue;
              }
            }

            return acc;
          }, null);

          if (hasValue(acc)) {
            return acc + (rigOperationPassingValue ?? 0);
          } else {
            return rigOperationPassingValue;
          }
        },
        null,
      );

      if (
        !hasValue(rigOperationsPassingValue) ||
        !hasValue(rigOperationsDrillingDuration) ||
        rigOperationsPassingValue <= 0 ||
        rigOperationsDrillingDuration <= 0
      ) {
        return;
      }

      const commercialSpeed = calculateCommercialSpeed(rigOperationsPassingValue, rigOperationsDrillingDuration);

      onValueChange('commercialSpeed', commercialSpeed, { control: mainDataCommercialSpeedControl });
    } else {
      const passingValueControl = fields['passingValue'];

      if (!passingValueControl) {
        console.error('passingValueControl is not presented');
        return;
      }

      const pasingValueValue = (() => {
        const value = passingValueControl.value;

        if (typeof value !== 'number' && value !== null && value !== undefined) {
          console.error('pasingValueValue value type is invalid');
          return null;
        }

        if (!hasValue(value)) {
          return null;
        }

        return value;
      })();

      if (
        !hasValue(pasingValueValue) ||
        !hasValue(rigOperationsDrillingDuration) ||
        pasingValueValue <= 0 ||
        rigOperationsDrillingDuration <= 0
      ) {
        return;
      }

      const commercialSpeed = calculateCommercialSpeed(pasingValueValue, rigOperationsDrillingDuration);
      onValueChange('commercialSpeed', commercialSpeed, { control: mainDataCommercialSpeedControl });
    }
  }

  private calculateDrillingStageDurationByCommercialSpeed(commSpeed: unknown): void {
    if (!this.form) {
      return;
    }

    const { rigOperationsService, fields } = this.form;

    if (!hasValue(commSpeed) || typeof commSpeed !== 'number') {
      return;
    }

    if (rigOperationsService.isSectionDetailingOrManyRigOperations) {
      return;
    }

    const { rigOperation, stage } = rigOperationsService.rigOperations.reduce(
      (acc: { rigOperation: RigOperation | null; stage: RigOperationStage | null }, currRigOperation) => {
        const drillingStage = currRigOperation.stages.find((stage) =>
          rigOperationsService.isDrillingStage(stage.wellLifeCycleStageId),
        );

        if (drillingStage) {
          acc.rigOperation = currRigOperation;
          acc.stage = drillingStage;
        }

        return acc;
      },
      {
        rigOperation: null,
        stage: null,
      },
    );

    if (!rigOperation || !stage) {
      return;
    }

    const passingValueControl = fields['passingValue'];

    if (!passingValueControl) {
      console.error('passingValueControl is not presented');
      return;
    }

    const pasingValueValue = (() => {
      const value = passingValueControl.value;

      if (typeof value !== 'number' && value !== null && value !== undefined) {
        console.error('pasingValueValue value type is invalid');
        return null;
      }

      if (!hasValue(value)) {
        return null;
      }

      return value;
    })();

    if (!hasValue(pasingValueValue) || pasingValueValue <= 0 || commSpeed <= 0) {
      return;
    }

    const duration = calculateDurationByCommercialSpeed(pasingValueValue, commSpeed);

    rigOperationsService.onValueChange('duration', duration, { rigOperation, stage });
  }
}
